/**
 * The report template, issue and cancel routes, as the web's Report templates
 * pages call them (step 6.3 and 6.4 of docs/plans/report-templates.md; wire
 * types in `@alluzo/shared/doc-api`). Kept on the web's side: the phone never
 * makes a document.
 *
 * A refusal is thrown as `DocRefused`, holding the envelope's code, its
 * sentence and the rest of the error body, because the Issue sheet reads more
 * of it than a sentence: the total check's two figures (D3, D16), the gaps,
 * the filed-return question.
 */
import type {
  DocCancelBody,
  DocIssueBody,
  DocIssueResponse,
  DocPreviewBody,
  DocPreviewResponse,
  DocStoredBody,
  DocStoredResponse,
  DocTemplateCreateBody,
  DocTemplateListResponse,
  DocTemplatePatch,
  DocTemplateResponse,
  DocTemplateSaveBody,
  IssuedDocumentListResponse,
  IssuedDocumentResponse,
} from '@alluzo/shared/doc-api';

import { fetch } from '~/platform/fetch';

import { apiBaseUrl, personHeaders, TENANT_HEADER } from '@/lib/api';
import { getCookie } from '@/lib/auth';

/** A refused call: the route's code, its sentence, and every other field of its error. */
export class DocRefused extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
    readonly body: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = 'DocRefused';
  }
}

const headers = (tenantId: string, body: boolean): Record<string, string> => ({
  ...personHeaders(getCookie()),
  [TENANT_HEADER]: tenantId,
  ...(body ? { 'content-type': 'application/json' } : {}),
});

/** A Retry-After a busy renderer answered (503), in seconds, or null. */
const retryAfterOf = (response: Response): number | null => {
  const raw = response.headers.get('retry-after');
  const seconds = raw === null ? NaN : Number(raw);
  return Number.isFinite(seconds) ? seconds : null;
};

const refusalOf = async (response: Response): Promise<DocRefused> => {
  const parsed = (await response.json().catch(() => null)) as { error?: Record<string, unknown> } | null;
  const error = parsed?.error ?? {};
  const message = typeof error.message === 'string' ? error.message : response.statusText;
  const retry = retryAfterOf(response);
  return new DocRefused(response.status, typeof error.code === 'string' ? error.code : null, message, retry === null ? error : { ...error, retryAfter: retry });
};

const send = async <T>(tenantId: string, path: string, method: 'GET' | 'POST' | 'PUT' | 'PATCH', body?: unknown): Promise<T> => {
  const response = await fetch(`${apiBaseUrl}/v1${path}`, {
    method,
    headers: headers(tenantId, body !== undefined),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw await refusalOf(response);
  return (await response.json()) as T;
};

/** A PDF answer and the name the server gave it. */
export type DocFile = { readonly blob: Blob; readonly name: string };

/** The file name in a content-disposition header, or the fallback. */
export const fileNameOf = (disposition: string | null, fallback: string): string => {
  if (disposition === null) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (star !== undefined) {
    try {
      return decodeURIComponent(star);
    } catch {
      /* the plain name below */
    }
  }
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? fallback;
};

const pdf = async (tenantId: string, path: string, method: 'GET' | 'POST', fallback: string, body?: unknown): Promise<DocFile> => {
  const response = await fetch(`${apiBaseUrl}/v1${path}`, {
    method,
    headers: { ...headers(tenantId, body !== undefined), accept: 'application/pdf, application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw await refusalOf(response);
  return { blob: await response.blob(), name: fileNameOf(response.headers.get('content-disposition'), fallback) };
};

const id = encodeURIComponent;

export const listTemplates = (tenantId: string, recordId: string): Promise<DocTemplateListResponse> =>
  send(tenantId, `/records/${id(recordId)}/doc-templates`, 'GET');

export const readTemplate = (tenantId: string, templateId: string): Promise<DocTemplateResponse> => send(tenantId, `/doc-templates/${id(templateId)}`, 'GET');

export const createTemplate = (tenantId: string, recordId: string, body: DocTemplateCreateBody): Promise<DocTemplateResponse> =>
  send(tenantId, `/records/${id(recordId)}/doc-templates`, 'POST', body);

export const saveTemplate = (tenantId: string, templateId: string, body: DocTemplateSaveBody): Promise<DocTemplateResponse> =>
  send(tenantId, `/doc-templates/${id(templateId)}`, 'PUT', body);

export const patchTemplate = (tenantId: string, templateId: string, body: DocTemplatePatch): Promise<DocTemplateResponse> =>
  send(tenantId, `/doc-templates/${id(templateId)}`, 'PATCH', body);

/** A card's wire layout as it would be stored, with the person's price basis answer kept (D15). */
export const storedLayoutOf = (tenantId: string, recordId: string, body: DocStoredBody): Promise<DocStoredResponse> =>
  send(tenantId, `/records/${id(recordId)}/doc-templates/stored`, 'POST', body);

export const previewDocument = (tenantId: string, recordId: string, body: DocPreviewBody): Promise<DocPreviewResponse> =>
  send(tenantId, `/records/${id(recordId)}/doc-templates/preview`, 'POST', body);

export const issueDocument = (tenantId: string, templateId: string, body: DocIssueBody): Promise<DocIssueResponse> =>
  send(tenantId, `/doc-templates/${id(templateId)}/issue`, 'POST', body);

/** An unnumbered kind's PDF, made now from the entry (a bill, a receipt, a quotation). */
export const renderDocumentFile = (tenantId: string, templateId: string, entryId: string, fallback: string): Promise<DocFile> =>
  pdf(tenantId, `/doc-templates/${id(templateId)}/render`, 'POST', fallback, { entryId });

/** An issued document's stored copy. */
export const issuedFile = (tenantId: string, issuedId: string, fallback: string, copy?: string): Promise<DocFile> =>
  pdf(tenantId, `/issued-documents/${id(issuedId)}/file${copy === undefined ? '' : `?copy=${id(copy)}`}`, 'GET', fallback);

export const listIssued = (tenantId: string, recordId: string, filter: { entryId?: string; status?: 'issued' | 'cancelled' } = {}): Promise<IssuedDocumentListResponse> => {
  const query = new URLSearchParams();
  if (filter.entryId !== undefined) query.set('entryId', filter.entryId);
  if (filter.status !== undefined) query.set('status', filter.status);
  const tail = query.size === 0 ? '' : `?${query.toString()}`;
  return send(tenantId, `/records/${id(recordId)}/issued-documents${tail}`, 'GET');
};

export const cancelIssued = (tenantId: string, issuedId: string, body: DocCancelBody): Promise<IssuedDocumentResponse> =>
  send(tenantId, `/issued-documents/${id(issuedId)}/cancel`, 'POST', body);
